import { useThirdPartyFeedbackIntegration } from 'api/integrationsConfig'
import { useGetLocationConfigsSummaryQuery } from 'api/recoveryAi'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import type { OverviewSignals, RaiCoverage } from './resolveOverviewState'

export type UseOverviewSignalsResult = {
  signals: OverviewSignals
  // Only present once a real, settled location count exists — the resolver
  // decides whether to SHOW a badge, but never invents the numbers in it.
  locationBadgeValues?: { enabled: number; total: number }
}

// Resolves the three real signals resolveOverviewState needs. Every signal
// below documents its own deployment caveat inline; this hook is the one
// place all three come together so Overview.tsx stays a pure consumer of
// OverviewSignals.
export const useOverviewSignals = (): UseOverviewSignalsResult => {
  const merchant = useCurrentMerchant()
  const recoveryAiEnabled = useFlag('recovery-ai', false)

  const {
    data: locationSummary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
  } = useGetLocationConfigsSummaryQuery(undefined, {
    skip: !recoveryAiEnabled,
  })

  // `allow_written_feedback_collection` (nexus#1938, open) is absent from the
  // API response until that PR deploys. undefined-field must resolve to the
  // documented server default (true) — not to the merchant-not-loaded
  // loading state, which is `merchant` itself being undefined.
  const isCollectingWrittenFeedback = merchant
    ? merchant.allow_written_feedback_collection ?? true
    : undefined

  // Stays undefined when the request fails, like the RecoveryAI summary error
  // below: null would claim "no tool" and give a GuestXM merchant the `new`
  // state's copy. The endpoint is ungated, so only a transient failure lands
  // here, and the next component to subscribe (a tab switch, a reload)
  // refetches an errored query.
  const { thirdPartyFeedbackProvider: thirdPartyProvider } =
    useThirdPartyFeedbackIntegration()

  let raiCoverage: RaiCoverage | undefined
  let locationBadgeValues: UseOverviewSignalsResult['locationBadgeValues']
  if (!recoveryAiEnabled) {
    raiCoverage = 'none'
  } else if (isSummaryError) {
    // The coverage endpoint is unavailable — not deployed yet, or refused.
    // A refusal is the common case rather than the exotic one: the summary is
    // gated on merchant_settings:view, while the sibling status endpoint this
    // page also reads is deliberately ungated, so a merchant user can be
    // entitled to this page and still be refused the counts.
    //
    // Stay unknown, not partial. This endpoint gave no evidence RecoveryAI is
    // covering any locations, so treating the error as partial coverage would
    // resolve to the confirmed rai_thanx/rai_thirdparty copy on no evidence.
    // undefined here folds into the same "not settled yet" branch as loading,
    // which is what LOADING_CONTENT already exists to represent.
    raiCoverage = undefined
  } else if (isSummaryLoading || !locationSummary) {
    raiCoverage = undefined
  } else {
    const { enabledCount, totalCount } = locationSummary
    if (totalCount === 0 || enabledCount === 0) {
      // No locations exist, or RecoveryAI is on for the merchant but
      // switched on at none of them — either way it is running nowhere.
      // The location badge only ever renders for partial coverage (see
      // buildHero's rai_thanx case), so leaving locationBadgeValues unset
      // here is a deliberate choice, not an omission: there is no count
      // worth showing when coverage is none.
      raiCoverage = 'none'
    } else if (enabledCount < totalCount) {
      raiCoverage = 'partial'
      locationBadgeValues = { enabled: enabledCount, total: totalCount }
    } else {
      raiCoverage = 'full'
      locationBadgeValues = { enabled: enabledCount, total: totalCount }
    }
  }

  return {
    signals: { isCollectingWrittenFeedback, thirdPartyProvider, raiCoverage },
    locationBadgeValues,
  }
}
